import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Input } from '../ui/Input';
import { Button } from '../ui/Button';
import PasswordInput from '../ui/PasswordInput';
import { entrepriseService } from '../../lib/api';
import { validatePhone, formatPhone } from '../../lib/utils';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    raison_sociale: '',
    nif: '',
    rccm: '',
    adresse: '',
    contact_nom: '',
    contact_telephone: '',
    contact_email: '',
    mode_facturation: 'PREPAY',
    nb_employes: '',
    password: '',
    confirm_password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [acceptCGU, setAcceptCGU] = useState(false);
  const [acceptConfidentialite, setAcceptConfidentialite] = useState(false);
  const navigate = useNavigate();

  // Fonction pour calculer la force du mot de passe
  const calculatePasswordStrength = (password) => {
    if (!password) return { level: 0, label: '', color: 'gray' };
    
    let strength = 0;
    if (password.length >= 6) strength += 1;
    if (password.length >= 10) strength += 1;
    if (/[a-z]/.test(password) && /[A-Z]/.test(password)) strength += 1;
    if (/\d/.test(password)) strength += 1;
    if (/[^a-zA-Z0-9]/.test(password)) strength += 1;

    if (strength <= 1) return { level: 1, label: 'Faible', color: 'red' };
    if (strength <= 2) return { level: 2, label: 'Moyen', color: 'orange' };
    if (strength <= 3) return { level: 3, label: 'Bon', color: 'yellow' };
    if (strength <= 4) return { level: 4, label: 'Fort', color: 'green' };
    return { level: 5, label: 'Très fort', color: 'green' };
  };

  const passwordStrength = calculatePasswordStrength(formData.password);

  // Calculer le coût total avec structure par paliers
  const calculateTotalCost = (nbEmployes) => {
    const employeeCount = parseInt(nbEmployes) || 0;
    
    if (employeeCount <= 50) {
      // De 0 à 50 employés : frais fixes de 3.000.000 GNF
      return 3000000;
    } else {
      // Au-delà de 50 employés : 3.000.000 + 10.000 par employé supplémentaire
      const baseCost = 3000000;
      const additionalEmployees = employeeCount - 50;
      const additionalCost = additionalEmployees * 10000;
      return baseCost + additionalCost;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-GN', {
      style: 'currency',
      currency: 'GNF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validation des CGU et politique de confidentialité
    if (!acceptCGU) {
      setError('Vous devez accepter les Conditions Générales d\'Utilisation pour continuer');
      setIsLoading(false);
      return;
    }

    if (!acceptConfidentialite) {
      setError('Vous devez accepter la Politique de Confidentialité pour continuer');
      setIsLoading(false);
      return;
    }

    // Validation
    if (!validatePhone(formData.contact_telephone)) {
      setError('Numéro de téléphone invalide (format guinéen requis)');
      setIsLoading(false);
      return;
    }

    if (!formData.password || formData.password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      setIsLoading(false);
      return;
    }

    if (formData.password !== formData.confirm_password) {
      setError('Les mots de passe ne correspondent pas');
      setIsLoading(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        contact_telephone: formatPhone(formData.contact_telephone)
      };

      await entrepriseService.register(submitData);
      setSuccess(true);
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (error) {
      setError(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4" 
           style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 30%, #EC4899 60%, #F97316 100%)' }}>
        <div className="max-w-md w-full">
          <div className="flex flex-col items-center mb-6">
            <img 
              src="/logo-alo-advance.png" 
              alt="ALO ADVANCE" 
              className="h-32 w-32 object-contain drop-shadow-2xl mb-2"
            />
            <p className="text-white text-base font-semibold italic">Parce que Nous prévoyons Vos imprévus</p>
          </div>
          <div className="bg-white rounded-2xl shadow-2xl p-8 text-center">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-500 mb-4">
              Inscription réussie !
            </h2>
            <p className="text-gray-600 mb-4">
              Votre entreprise a été enregistrée avec succès. 
              Vous allez être redirigé vers la page de connexion.
            </p>
            <div className="p-4 bg-gradient-to-r from-purple-50 to-orange-50 border-l-4 border-purple-500 rounded-r-lg">
              <p className="text-sm text-purple-700">
                💼 Un administrateur ALO validera votre compte sous peu.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8" 
         style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #A855F7 30%, #EC4899 60%, #F97316 100%)' }}>
      <div className="max-w-2xl w-full space-y-8">
        {/* Logo et slogan */}
        <div className="text-center">
          <div className="flex flex-col items-center mb-6">
            <img 
              src="/logo-alo-advance.png" 
              alt="ALO ADVANCE" 
              className="h-32 w-32 object-contain drop-shadow-2xl mb-2"
            />
            <p className="text-white text-base font-semibold italic">Parce que Nous prévoyons Vos imprévus</p>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">
            Inscription Entreprise
          </h2>
          <p className="mt-2 text-sm text-purple-100">
            Rejoignez ALO ADVANCE pour gérer les avances de vos employés
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="p-8">
            <h3 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-500 mb-6">
              Informations de l'entreprise
            </h3>
            
            {error && (
              <div className="mb-4 p-4 bg-red-50 border-l-4 border-red-500 text-red-700 rounded-r-lg">
                <div className="flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
                  </svg>
                  {error}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="raison_sociale" className="block text-sm font-medium text-gray-700 mb-1">
                    Raison sociale *
                  </label>
                  <Input
                    id="raison_sociale"
                    name="raison_sociale"
                    type="text"
                    value={formData.raison_sociale}
                    onChange={handleChange}
                    placeholder="Nom de votre entreprise"
                    required
                  />
                </div>

                <div>
                  <label htmlFor="nif" className="block text-sm font-medium text-gray-700 mb-1">
                    NIF <span className="text-gray-500 text-xs">(optionnel)</span>
                  </label>
                  <Input
                    id="nif"
                    name="nif"
                    type="text"
                    value={formData.nif}
                    onChange={handleChange}
                    placeholder="Numéro d'Identification Fiscale (optionnel)"
                  />
                </div>

                <div>
                  <label htmlFor="rccm" className="block text-sm font-medium text-gray-700 mb-1">
                    RCCM *
                  </label>
                  <Input
                    id="rccm"
                    name="rccm"
                    type="text"
                    value={formData.rccm}
                    onChange={handleChange}
                    placeholder="Registre de Commerce"
                    required
                  />
                </div>
              </div>

              <div>
                <label htmlFor="adresse" className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse *
                </label>
                <Input
                  id="adresse"
                  name="adresse"
                  type="text"
                  value={formData.adresse}
                  onChange={handleChange}
                  placeholder="Adresse complète de l'entreprise"
                  required
                />
              </div>

              <div className="border-t border-purple-100 pt-6">
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500 mb-4">
                  Contact principal
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact_nom" className="block text-sm font-medium text-gray-700 mb-1">
                      Nom du contact *
                    </label>
                    <Input
                      id="contact_nom"
                      name="contact_nom"
                      type="text"
                      value={formData.contact_nom}
                      onChange={handleChange}
                      placeholder="Nom et prénom"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="contact_telephone" className="block text-sm font-medium text-gray-700 mb-1">
                      Téléphone Contact *
                    </label>
                    <Input
                      id="contact_telephone"
                      name="contact_telephone"
                      type="tel"
                      value={formData.contact_telephone}
                      onChange={handleChange}
                      placeholder="621234567 (9 chiffres)"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ℹ️ Ce numéro doit porter un wallet Mobile Money pour les notifications SMS
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <label htmlFor="contact_email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <Input
                    id="contact_email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleChange}
                    placeholder="contact@entreprise.com"
                    required
                  />
                </div>
              </div>

              <div className="border-t border-purple-100 pt-6">
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-orange-500 mb-4">
                  Mode de facturation
                </h3>
                
                <div className="space-y-3">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mode_facturation"
                      value="PREPAY"
                      checked={formData.mode_facturation === 'PREPAY'}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Prépaiement</div>
                      <div className="text-sm text-gray-600">
                        Vous créditez un solde chez ALO FINANCES avant de pouvoir accorder des avances
                      </div>
                    </div>
                  </label>
                  
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="mode_facturation"
                      value="POSTPAY"
                      checked={formData.mode_facturation === 'POSTPAY'}
                      onChange={handleChange}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium">Post-paiement</div>
                      <div className="text-sm text-gray-600">
                        Vous payez après que les avances aient été accordées (facturation mensuelle)
                      </div>
                    </div>
                  </label>
                </div>

                {/* Calculateur de coût */}
                <div className="mt-6 p-4 bg-gradient-to-r from-purple-100 to-orange-100 rounded-lg border-2 border-purple-300">
                  <h4 className="font-bold text-purple-700 mb-3 flex items-center">
                    <span className="text-2xl mr-2">💰</span>
                    Estimation des frais de déploiement annuels
                  </h4>
                  
                  <div className="mb-3 p-3 bg-white/70 rounded border-l-4 border-purple-500">
                    <p className="text-xs font-semibold text-purple-700 mb-1">📊 Structure tarifaire :</p>
                    <ul className="text-xs text-gray-700 space-y-1">
                      <li>• <strong>0 à 50 employés :</strong> 3.000.000 GNF (forfait)</li>
                      <li>• <strong>Au-delà de 50 :</strong> 3.000.000 GNF + 10.000 GNF par employé supplémentaire</li>
                    </ul>
                  </div>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700 font-medium">Nombre d'employés :</span>
                      <input 
                        type="number" 
                        min="0"
                        placeholder="0"
                        value={formData.nb_employes || ''}
                        onChange={(e) => setFormData({...formData, nb_employes: e.target.value})}
                        className="w-24 px-3 py-2 border-2 border-purple-300 rounded-lg text-center font-bold text-purple-700 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                    
                    {parseInt(formData.nb_employes) > 50 && (
                      <div className="bg-orange-50 p-2 rounded">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-gray-700">Forfait (0-50 employés) :</span>
                          <span className="font-semibold text-purple-700">3.000.000 GNF</span>
                        </div>
                        <div className="flex justify-between items-center text-xs mt-1">
                          <span className="text-gray-700">
                            Employés supplémentaires ({parseInt(formData.nb_employes) - 50} × 10.000) :
                          </span>
                          <span className="font-semibold text-orange-600">
                            {formatCurrency((parseInt(formData.nb_employes) - 50) * 10000)}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    <div className="border-t-2 border-purple-300 pt-3 mt-3">
                      <div className="flex justify-between items-center">
                        <span className="font-bold text-purple-800 text-lg">TOTAL À PAYER :</span>
                        <span className="font-bold text-orange-600 text-2xl">
                          {formatCurrency(calculateTotalCost(formData.nb_employes))}
                        </span>
                      </div>
                      <p className="text-xs text-gray-600 text-right mt-1">par an</p>
                    </div>
                  </div>

                  <div className="mt-3 p-3 bg-white rounded border-l-4 border-orange-500">
                    <p className="text-xs text-gray-600">
                      ℹ️ <strong>Important :</strong> Ce montant devra être réglé <strong>avant la validation</strong> de votre compte. 
                      Vous recevrez les instructions de paiement par email immédiatement après votre inscription. 
                      L'administrateur ALO ADVANCE validera votre compte après vérification de votre KYC et du paiement.
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-t border-purple-100 pt-6">
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-500 mb-4">
                  Accès au compte
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Définissez le mot de passe pour accéder à votre tableau de bord entreprise
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Mot de passe *
                    </label>
                    <PasswordInput
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                    />
                    
                    {/* Indicateur de force du mot de passe */}
                    {formData.password && (
                      <div className="mt-2">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-gray-600">Force du mot de passe :</span>
                          <span className={`text-xs font-semibold ${
                            passwordStrength.color === 'red' ? 'text-red-600' :
                            passwordStrength.color === 'orange' ? 'text-orange-600' :
                            passwordStrength.color === 'yellow' ? 'text-yellow-600' :
                            'text-green-600'
                          }`}>
                            {passwordStrength.label}
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all duration-300 ${
                              passwordStrength.color === 'red' ? 'bg-red-500' :
                              passwordStrength.color === 'orange' ? 'bg-orange-500' :
                              passwordStrength.color === 'yellow' ? 'bg-yellow-500' :
                              'bg-green-500'
                            }`}
                            style={{ width: `${(passwordStrength.level / 5) * 100}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Utilisez majuscules, minuscules, chiffres et caractères spéciaux
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
                      Confirmer le mot de passe *
                    </label>
                    <PasswordInput
                      id="confirm_password"
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Section Protection des données */}
              <div className="border-t border-purple-100 pt-6">
                <h3 className="text-lg font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-orange-500 mb-4">
                  Protection des données personnelles
                </h3>
                
                <div className="space-y-4 bg-purple-50 p-4 rounded-lg">
                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="acceptCGU"
                      checked={acceptCGU}
                      onChange={(e) => setAcceptCGU(e.target.checked)}
                      className="mt-1 mr-3 h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      required
                    />
                    <label htmlFor="acceptCGU" className="text-sm text-gray-700">
                      <span className="font-semibold text-purple-700">J'accepte les </span>
                      <a 
                        href="/cgu" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-orange-600 underline font-semibold"
                      >
                        Conditions Générales d'Utilisation
                      </a>
                      <span className="text-red-600 font-bold"> *</span>
                    </label>
                  </div>

                  <div className="flex items-start">
                    <input
                      type="checkbox"
                      id="acceptConfidentialite"
                      checked={acceptConfidentialite}
                      onChange={(e) => setAcceptConfidentialite(e.target.checked)}
                      className="mt-1 mr-3 h-5 w-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
                      required
                    />
                    <label htmlFor="acceptConfidentialite" className="text-sm text-gray-700">
                      <span className="font-semibold text-purple-700">J'accepte la </span>
                      <a 
                        href="/politique-confidentialite" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-purple-600 hover:text-orange-600 underline font-semibold"
                      >
                        Politique de Confidentialité
                      </a>
                      <span className="text-red-600 font-bold"> *</span>
                    </label>
                  </div>

                  <div className="mt-3 p-3 bg-white border-l-4 border-orange-500 rounded-r">
                    <p className="text-xs text-gray-600">
                      🔒 <strong>Vos données sont protégées.</strong> Nous nous engageons à respecter votre vie privée 
                      et à traiter vos données personnelles conformément au RGPD et aux lois guinéennes en vigueur.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex space-x-4">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-2 border-purple-600 text-purple-600 hover:bg-purple-50"
                  onClick={() => navigate('/login')}
                >
                  Retour à la connexion
                </Button>
                <Button
                  type="submit"
                  className="flex-1 py-3 bg-gradient-to-r from-purple-600 via-pink-500 to-orange-500 hover:from-purple-700 hover:via-pink-600 hover:to-orange-600 text-white font-semibold rounded-lg shadow-lg transition-all transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  disabled={isLoading || !acceptCGU || !acceptConfidentialite}
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Inscription en cours...
                    </span>
                  ) : "S'inscrire"}
                </Button>
              </div>
            </form>

            <div className="mt-6 p-4 bg-gradient-to-r from-purple-50 to-orange-50 border-l-4 border-purple-500 rounded-r-lg">
              <p className="text-sm text-purple-800">
                <strong>💡 Processus d'activation :</strong>
              </p>
              <ol className="text-sm text-gray-700 mt-2 ml-4 space-y-1">
                <li>1️⃣ Vous recevrez un email avec les instructions de paiement</li>
                <li>2️⃣ Effectuez le paiement des frais de déploiement ({formatCurrency(calculateTotalCost(formData.nb_employes))})</li>
                <li>3️⃣ Envoyez la preuve de paiement à ALO FINANCES</li>
                <li>4️⃣ Votre compte sera validé sous 24-48h après vérification de votre KYC et du paiement</li>
              </ol>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;